import { ref, listAll, getDownloadURL, getBlob } from 'firebase/storage';
import { storage } from '../firebase/firebaseConfig';

// Check if we should use local templates (for testing)
const USE_LOCAL_TEMPLATES = import.meta.env.VITE_USE_LOCAL_TEMPLATES === 'true';
const USE_LOCAL_COVER_TEMPLATES = import.meta.env.VITE_USE_LOCAL_COVER_TEMPLATES === 'true';

/**
 * Dynamically discover all template files using Vite's glob import
 * This automatically detects any new templates added to the folders
 */
const discoverLocalTemplates = () => {
  // Use Vite's import.meta.glob to scan template folders
  // IMPORTANT: { as: 'raw' } prevents Vite from treating HTML files as entry points
  // and injecting the app's CSS bundle (<link href="/assets/style.xxx.css">) into them.
  // Without this, production builds inject app CSS that resets body { padding: 0 }
  // which strips template padding and breaks the live preview layout.
  const templateFiles = import.meta.glob('/src/templates/**/*.html', { eager: false, as: 'raw' });
  
  const templates = [];
  
  // Process each discovered template file
  for (const path in templateFiles) {
    // Skip backup folders
    if (path.includes('/backup/')) continue;
    
    // Extract tier and filename from path
    // Example path: /src/templates/silver/resume-silver-1.html
    const match = path.match(/\/templates\/(silver|gold|platinum)\/resume-(\w+)-(\d+)\.html$/);
    
    if (match) {
      const [, tier, tierName, number] = match;
      const filename = `resume-${tierName}-${number}.html`;
      
      templates.push({
        id: `${tier}-${number}`,
        name: `${tier.charAt(0).toUpperCase() + tier.slice(1)} Template ${number}`,
        plan: tier,
        filename: filename,
        number: number,
        path: path,
        loader: templateFiles[path] // Store the loader function for raw import
      });
    }
  }
  
  // Sort templates by tier and number
  templates.sort((a, b) => {
    const tierOrder = { platinum: 1, gold: 2, silver: 3 };
    if (tierOrder[a.plan] !== tierOrder[b.plan]) {
      return tierOrder[a.plan] - tierOrder[b.plan];
    }
    return parseInt(a.number) - parseInt(b.number);
  });
  
  return templates;
};

/**
 * Template metadata for local development (dynamically generated)
 */
const LOCAL_TEMPLATES = discoverLocalTemplates();

/**
 * Load all templates from local files (for testing)
 * @returns {Promise<Array>} Array of template objects with local paths
 */
const loadTemplatesFromLocal = async () => {
  try {
    const templates = LOCAL_TEMPLATES.map(({ loader, ...template }) => ({
      ...template,
      downloadURL: `/src/templates/${template.plan}/${template.filename}`,
      storagePath: `templates/${template.plan}/${template.filename}`,
      // Pre-rendered static preview HTML (generated at build time by scripts/prerender-template-previews.js)
      previewURL: `/template-previews/${template.plan}-${template.number}.html`,
      // NOTE: Do NOT include `loader` here — functions can't be serialized
      // through React Router's navigate() state (history.pushState).
      // The loader is kept in LOCAL_TEMPLATES and used by loadTemplateHTMLFromLocal.
      isLocal: true
    }));
    
    return templates;
  } catch (error) {
    console.error('❌ Error loading local templates:', error);
    return [];
  }
};

/**
 * Load all templates from Firebase Storage (OPTIMIZED)
 * Scans gold/, silver/, and platinum/ folders in parallel
 * @returns {Promise<Array>} Array of template objects with download URLs
 */
const loadTemplatesFromFirebase = async () => {
  const tiers = ['gold', 'silver', 'platinum'];

  try {
    // Load all tiers in parallel for faster loading
    const tierPromises = tiers.map(async (tier) => {
      try {
        const tierRef = ref(storage, `templates/${tier}`);
        const result = await listAll(tierRef);
        
        // Get all download URLs in parallel
        const urlPromises = result.items.map(async (itemRef) => {
          try {
            const downloadURL = await getDownloadURL(itemRef);
            const filename = itemRef.name;
            
            // Extract template number from filename
            const match = filename.match(/resume-\w+-(\d+)\.html/);
            const number = match ? match[1] : '0';
            
            return {
              id: `${tier}-${number}`,
              name: `${tier.charAt(0).toUpperCase() + tier.slice(1)} Template ${number}`,
              plan: tier,
              filename: filename,
              downloadURL: downloadURL,
              storagePath: itemRef.fullPath,
              // Pre-rendered static preview HTML (generated at build time by scripts/prerender-template-previews.js)
              previewURL: `/template-previews/${tier}-${number}.html`,
              isLocal: false
            };
          } catch (error) {
            console.error(`Error loading ${itemRef.name}:`, error);
            return null;
          }
        });
        
        const templates = await Promise.all(urlPromises);
        return templates.filter(t => t !== null);
      } catch (error) {
        console.error(`Error loading ${tier} templates:`, error);
        return [];
      }
    });
    
    // Wait for all tiers to load
    const tierResults = await Promise.all(tierPromises);
    const allTemplates = tierResults.flat();
    
    return allTemplates;
  } catch (error) {
    console.error('❌ Error loading templates:', error);
    return [];
  }
};

/**
 * Load all templates (from local files or Firebase based on env variable)
 * @returns {Promise<Array>} Array of template objects
 */
export const loadTemplatesFromStorage = async () => {
  if (USE_LOCAL_TEMPLATES) {
    return await loadTemplatesFromLocal();
  } else {
    return await loadTemplatesFromFirebase();
  }
};

/**
 * Load template HTML content from local files (for testing)
 * @param {string} templatePath - Local path (e.g., 'templates/silver/resume-silver-1.html')
 * @returns {Promise<string>} Template HTML content
 */
const loadTemplateHTMLFromLocal = async (templatePath) => {
  try {
    // Construct the local file path
    // Remove leading 'templates/' if present
    const cleanPath = templatePath.replace(/^templates\//, '');
    const localPath = `/src/templates/${cleanPath}`;
    
    // First, try to find a matching loader from the discovered templates.
    // Using the Vite glob loader (with { as: 'raw' }) returns the raw HTML
    // string WITHOUT Vite injecting app CSS links into the template.
    // This is critical for production builds — without it, the app's global
    // CSS (body { padding: 0 }) overrides the template's own body padding.
    const matchingTemplate = LOCAL_TEMPLATES.find(t => t.path === localPath);
    if (matchingTemplate?.loader) {
      const htmlContent = await matchingTemplate.loader();
      return htmlContent;
    }
    
    // Fallback: fetch from server (works in dev, may have CSS injection in prod)
    const response = await fetch(localPath);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    let htmlContent = await response.text();
    
    // Strip any Vite-injected CSS links that bleed app styles into the template
    // (safety net in case the raw import path wasn't used)
    htmlContent = htmlContent.replace(/<link\s+[^>]*href="\/assets\/[^"]*\.css"[^>]*>/gi, '');
    
    return htmlContent;
  } catch (error) {
    console.error('❌ Error loading local template HTML:', error);
    throw error;
  }
};

/**
 * Load template HTML content from Firebase Storage
 * Uses Firebase SDK to avoid CORS issues
 * @param {string} templatePath - Firebase Storage path (e.g., 'templates/silver/resume-silver-1.html')
 * @returns {Promise<string>} Template HTML content
 */
const loadTemplateHTMLFromFirebase = async (templatePath) => {
  try {
    // Create a reference to the file in Firebase Storage
    const templateRef = ref(storage, templatePath);
    
    try {
      // Try to download using getBlob (should work with proper Firebase config)
      const blob = await getBlob(templateRef);
      const htmlContent = await blob.text();
      
      return htmlContent;
    } catch (blobError) {
      // Fallback: Try using download URL with fetch
      const downloadURL = await getDownloadURL(templateRef);
      
      const response = await fetch(downloadURL, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        headers: {
          'Accept': 'text/html',
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const htmlContent = await response.text();
      return htmlContent;
    }
  } catch (error) {
    console.error('❌ Error loading template HTML:', error);
    
    // Check if it's a CORS error
    if (error.message.includes('CORS') || 
        error.message.includes('Failed to fetch') ||
        error.name === 'TypeError') {
      console.error('🚫 CORS Error: Firebase Storage CORS not configured properly');
      console.error('📖 See CORS_FIX_INSTRUCTIONS.md for setup guide');
      throw new Error('CORS_ERROR');
    }
    
    throw error;
  }
};

/**
 * Load template HTML content (from local files or Firebase based on env variable)
 * @param {string} templatePath - Template path (e.g., 'templates/silver/resume-silver-1.html')
 * @returns {Promise<string>} Template HTML content
 */
export const loadTemplateHTML = async (templatePath) => {
  if (USE_LOCAL_TEMPLATES) {
    return await loadTemplateHTMLFromLocal(templatePath);
  } else {
    return await loadTemplateHTMLFromFirebase(templatePath);
  }
};

/**
 * Get default/fallback template HTML
 * @returns {string} Basic HTML template
 */
export const getDefaultTemplateHTML = () => {
  return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Resume</title>
    <style>
        body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
        h1 { color: #333; margin-bottom: 10px; }
        h2 { color: #555; border-bottom: 2px solid #333; padding-bottom: 5px; margin-top: 30px; }
        .contact-info { color: #666; margin-bottom: 30px; }
        .section { margin-bottom: 25px; }
    </style>
</head>
<body>
    <h1>{{fullName}}</h1>
    <div class="contact-info">
        <p>{{email}} | {{phone}} | {{location}}</p>
        <p>{{linkedin}} | {{github}} | {{website}}</p>
    </div>
    
    <div class="section">
        <h2>Professional Summary</h2>
        <p>{{summary}}</p>
    </div>
    
    <div class="section">
        <h2>Work Experience</h2>
        <!-- Experience items will be injected here -->
    </div>
    
    <div class="section">
        <h2>Education</h2>
        <!-- Education items will be injected here -->
    </div>
    
    <div class="section">
        <h2>Skills</h2>
        <p>{{skills}}</p>
    </div>
    
    <div class="section">
        <h2>Projects</h2>
        <!-- Project items will be injected here -->
    </div>
    
    <div class="section">
        <h2>Certifications</h2>
        <!-- Certification items will be injected here -->
    </div>
</body>
</html>
  `;
};

/**
 * Test Firebase Storage connection
 * @returns {Promise<Object>} Connection test results
 */
export const testStorageConnection = async () => {
  const results = {
    connected: false,
    bucketName: null,
    error: null,
    canListFiles: false,
    samplePath: null
  };

  try {
    // Get storage bucket info
    results.bucketName = storage.app.options.storageBucket;
    
    // Try to list files in templates/gold
    const testRef = ref(storage, 'templates/gold');
    
    const result = await listAll(testRef);
    results.canListFiles = true;
    results.samplePath = result.items[0]?.fullPath || 'No files found';
    
    results.connected = true;
    
  } catch (error) {
    console.error('❌ Storage connection test failed:', error);
    results.error = {
      code: error.code,
      message: error.message,
      name: error.name
    };
  }
  
  return results;
};

/**
 * ====================================
 * COVER LETTER TEMPLATE FUNCTIONS
 * ====================================
 */

/**
 * Dynamically discover all cover letter template files using Vite's glob import
 */
const discoverLocalCoverLetterTemplates = () => {
  // Use Vite's import.meta.glob to scan cover letter template folders
  const templateFiles = import.meta.glob('/src/templates/cover-letter/**/*.html', { eager: false, as: 'raw' });
  
  const templates = [];
  
  // Process each discovered template file
  for (const path in templateFiles) {
    // Extract tier and filename from path
    // Example path: /src/templates/cover-letter/silver/CL-silver-1.html
    const match = path.match(/\/cover-letter\/(silver|gold|platinum)\/CL-(\w+)-(\d+)\.html$/);
    
    if (match) {
      const [, tier, tierName, number] = match;
      const filename = `CL-${tierName}-${number}.html`;
      
      templates.push({
        id: `cl-${tier}-${number}`,
        name: `${tier.charAt(0).toUpperCase() + tier.slice(1)} Cover Letter ${number}`,
        plan: tier,
        filename: filename,
        number: number,
        path: path,
        loader: templateFiles[path] // Store the loader function for dynamic import
      });
    }
  }
  
  // Sort templates by tier and number
  templates.sort((a, b) => {
    const tierOrder = { platinum: 1, gold: 2, silver: 3 };
    if (tierOrder[a.plan] !== tierOrder[b.plan]) {
      return tierOrder[a.plan] - tierOrder[b.plan];
    }
    return parseInt(a.number) - parseInt(b.number);
  });
  
  return templates;
};

/**
 * Template metadata for local cover letter templates
 */
const LOCAL_COVER_LETTER_TEMPLATES = discoverLocalCoverLetterTemplates();

/**
 * Load all cover letter templates from local files
 * @returns {Promise<Array>} Array of template objects with local paths
 */
const loadCoverLetterTemplatesFromLocal = async () => {
  try {
    const templates = LOCAL_COVER_LETTER_TEMPLATES.map(template => ({
      ...template,
      downloadURL: `/src/templates/cover-letter/${template.plan}/${template.filename}`,
      storagePath: `templates/cover-letter/${template.plan}/${template.filename}`,
      isLocal: true,
      loader: template.loader, // Include the loader function for dynamic import
      description: getCoverLetterDescription(template.plan, parseInt(template.number))
    }));
    
    return templates;
  } catch (error) {
    console.error('❌ Error loading local cover letter templates:', error);
    return [];
  }
};

/**
 * Get description for cover letter template
 */
const getCoverLetterDescription = (tier, number) => {
  const descriptions = {
    silver: [
      'Professional and clean design perfect for any industry',
      'Modern layout with emphasis on key qualifications',
      'Traditional format ideal for corporate roles',
      'Straightforward design highlighting your experience',
      'Classic style with professional appearance',
      'Centered header with sharp corporate aesthetics',
      'Two-column header with achievement highlight',
      'Bold dark banner with subject line reference'
    ],
    gold: [
      'Elegant design with premium formatting',
      'Sophisticated layout for executive positions',
      'Modern premium style with visual appeal',
      'Professional design with enhanced aesthetics',
      'Refined format for senior-level applications',
      'Gradient accent with notable achievement card',
      'Side-accent bar with split-column signature',
      'Full gradient banner with two-column strengths'
    ],
    platinum: [
      'Premium executive design with luxury appeal',
      'High-end format for C-level positions',
      'Ultimate professional presentation',
      'Top-tier design with maximum impact',
      'Elite format for distinguished professionals',
      'Side-accent with three-bar footer motif',
      'Dark header with achievement card showcase',
      'Elegant double-border frame with executive layout'
    ]
  };

  return descriptions[tier]?.[number - 1] || `${tier.charAt(0).toUpperCase() + tier.slice(1)} tier cover letter template`;
};

/**
 * Load all cover letter templates from Firebase Storage
 * @returns {Promise<Array>} Array of template objects with download URLs
 */
const loadCoverLetterTemplatesFromFirebase = async () => {
  const tiers = ['silver', 'gold', 'platinum'];

  try {
    // Load all tiers in parallel for faster loading
    const tierPromises = tiers.map(async (tier) => {
      try {
        const tierRef = ref(storage, `templates/cover-letter/${tier}`);
        console.log(`📂 Listing cover letter templates: templates/cover-letter/${tier}`);
        const result = await listAll(tierRef);
        console.log(`📋 Found ${result.items.length} items in cover-letter/${tier}`);
        
        if (result.items.length === 0) {
          console.warn(`⚠️ No files found in templates/cover-letter/${tier}`);
          return [];
        }
        
        // Get all download URLs in parallel
        const urlPromises = result.items.map(async (itemRef) => {
          try {
            const downloadURL = await getDownloadURL(itemRef);
            const filename = itemRef.name;
            
            // Extract template number from filename
            const match = filename.match(/CL-\w+-(\d+)\.html/);
            const number = match ? match[1] : '0';
            
            return {
              id: `cl-${tier}-${number}`,
              name: `${tier.charAt(0).toUpperCase() + tier.slice(1)} Cover Letter ${number}`,
              plan: tier,
              filename: filename,
              downloadURL: downloadURL,
              storagePath: itemRef.fullPath,
              isLocal: false,
              description: getCoverLetterDescription(tier, parseInt(number))
            };
          } catch (error) {
            console.error(`Error loading ${itemRef.name}:`, error);
            return null;
          }
        });
        
        const templates = await Promise.all(urlPromises);
        return templates.filter(t => t !== null);
      } catch (error) {
        console.error(`Error loading ${tier} cover letter templates:`, error);
        return [];
      }
    });
    
    // Wait for all tiers to load
    const tierResults = await Promise.all(tierPromises);
    const allTemplates = tierResults.flat();
    
    return allTemplates;
  } catch (error) {
    console.error('❌ Error loading cover letter templates:', error);
    return [];
  }
};

/**
 * Load all cover letter templates (from local files or Firebase based on env variable)
 * @returns {Promise<Array>} Array of cover letter template objects
 */
export const loadCoverLetterTemplates = async () => {
  if (USE_LOCAL_COVER_TEMPLATES) {
    return await loadCoverLetterTemplatesFromLocal();
  } else {
    return await loadCoverLetterTemplatesFromFirebase();
  }
};

/**
 * Load cover letter template HTML content from local files
 * @param {string} templatePath - Local path (e.g., 'templates/cover-letter/silver/CL-silver-1.html')
 * @returns {Promise<string>} Template HTML content
 */
const loadCoverLetterTemplateHTMLFromLocal = async (templatePath) => {
  try {
    const cleanPath = templatePath.replace(/^templates\//, '');
    
    // Try to find a matching loader from discovered templates
    const matchingTemplate = LOCAL_COVER_LETTER_TEMPLATES.find(t => {
      const tCleanPath = `cover-letter/${t.plan}/${t.filename}`;
      return cleanPath === tCleanPath || 
             cleanPath.includes(t.filename) || 
             templatePath.includes(t.filename);
    });
    
    if (matchingTemplate && matchingTemplate.loader) {
      const html = await matchingTemplate.loader();
      return html;
    }
    
    // Fallback: try direct fetch from local dev server
    const fetchPath = `/src/templates/${cleanPath}`;
    const response = await fetch(fetchPath);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    return await response.text();
  } catch (error) {
    console.error('❌ Error loading local cover letter template HTML:', error);
    throw error;
  }
};

/**
 * Load cover letter template HTML content from Firebase Storage
 * Uses Firebase SDK to avoid CORS issues
 * @param {string} templatePath - Firebase Storage path (e.g., 'templates/cover-letter/silver/CL-silver-1.html')
 * @returns {Promise<string>} Template HTML content
 */
const loadCoverLetterTemplateHTMLFromFirebase = async (templatePath) => {
  try {
    const templateRef = ref(storage, templatePath);
    
    try {
      // Try getBlob first (Firebase SDK, avoids CORS)
      const blob = await getBlob(templateRef);
      return await blob.text();
    } catch (blobError) {
      // Fallback: download URL + fetch
      const downloadURL = await getDownloadURL(templateRef);
      const response = await fetch(downloadURL, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        headers: { 'Accept': 'text/html' }
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return await response.text();
    }
  } catch (error) {
    console.error('❌ Error loading cover letter template HTML from Firebase:', error);
    if (error.message.includes('CORS') || 
        error.message.includes('Failed to fetch') ||
        error.name === 'TypeError') {
      console.error('🚫 CORS Error: Firebase Storage CORS not configured properly');
      throw new Error('CORS_ERROR');
    }
    throw error;
  }
};

/**
 * Load cover letter template HTML content (from local files or Firebase based on env variable)
 * @param {string} templatePath - Template path (e.g., 'templates/cover-letter/silver/CL-silver-1.html')
 * @returns {Promise<string>} Template HTML content
 */
export const loadCoverLetterTemplateHTML = async (templatePath) => {
  if (USE_LOCAL_COVER_TEMPLATES) {
    return await loadCoverLetterTemplateHTMLFromLocal(templatePath);
  } else {
    return await loadCoverLetterTemplateHTMLFromFirebase(templatePath);
  }
};

